import React from "react";
import {
  Paper,
  Grid,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  Button,
} from "@mui/material";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import RemoveCircleIcon from "@mui/icons-material/RemoveCircle";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useSelector } from "react-redux";

export default function UltimateFanForm({
  component,
  onUpdate,
  onDelete,
  onUpdateHandler,
  disabled = false,
}) {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: component.homeComponentItemId });

  const tenants = useSelector((state) => state.tenant?.tenants || []);

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const handleTenantChange = (e) => {
    onUpdate(component.homeComponentItemId, {
      ...component,
      tenantId: e.target.value,
    });
  };

  const isIdString = typeof component.homeComponentItemId === "string";
  const isDisabled = disabled || isIdString;

  return (
    <Paper
      ref={setNodeRef}
      style={style}
      sx={{
        backgroundColor: "white",
        borderRadius: "16px",
        boxShadow: "0.5px 3px 10px 0px rgba(119,119,119,0.1)",
        mb: 2,
        p: 2,
      }}
    >
      <Grid
        container
        spacing={2}
        alignItems="center"
        sx={{ minHeight: "80px" }}
      >
        {/* Drag Handle */}
        <Grid item xs={1}>
          <IconButton
            {...attributes}
            {...listeners}
            disabled={disabled}
            sx={{
              cursor: disabled ? "not-allowed" : "grab",
              color: disabled ? "#ccc" : "#7F63F4",
              "&:active": {
                cursor: disabled ? "not-allowed" : "grabbing",
              },
            }}
          >
            <DragIndicatorIcon />
          </IconButton>
        </Grid>

        {/* Tenant Dropdown */}
        <Grid item xs={3}>
          <FormControl fullWidth size="small" variant="standard">
            <Select
              value={component.tenantId ?? ""}
              onChange={handleTenantChange}
              disabled={disabled}
              displayEmpty
              sx={{
                fontSize: "16px",
                fontWeight: 600,
                color: "#667380",
                "&:before": {
                  borderBottom: "1px solid #E0E0E0",
                },
                "&:hover:before": {
                  borderBottom: "1px solid #667380",
                },
                "&:after": {
                  borderBottom: "2px solid #667380",
                },
                "& .MuiSelect-select": {
                  paddingBottom: "8px",
                },
              }}
            >
              {tenants.map((tenant) => (
                <MenuItem key={tenant.tenantId} value={tenant.tenantId}>
                  {tenant.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        {/* Spacer */}
        <Grid item xs />

        {/* Action Buttons */}
        <Grid
          item
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 1,
            ml: "auto",
          }}
        >
          <Button
            variant="outlined"
            onClick={() =>
              onUpdateHandler(component.homeComponentItemId, component)
            }
            disabled={isDisabled}
            sx={{
              borderColor: "#3167ca",
              color: "#3167ca",
              fontWeight: 700,
              fontSize: "17.143px",
              borderRadius: "8px",
              boxShadow: "0px 4px 10px 0px rgba(0,0,0,0.15)",
              minWidth: "107px",
              height: "47px",
              textTransform: "none",
              "&:hover": {
                borderColor: "#3167ca",
                backgroundColor: "rgba(49, 103, 202, 0.04)",
              },
            }}
          >
            Update
          </Button>
          <IconButton
            onClick={() => onDelete(component.homeComponentItemId)}
            disabled={isDisabled}
            sx={{
              color: isDisabled ? "#ccc" : "#FF5274",
              "&:hover": {
                backgroundColor: isIdString
                  ? "transparent"
                  : "rgba(255, 82, 116, 0.08)",
              },
            }}
          >
            <RemoveCircleIcon sx={{ fontSize: "30px" }} />
          </IconButton>
        </Grid>
      </Grid>
    </Paper>
  );
}
